'use client'

import { curriculumTypeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { isAiLearningAvailableForCountry } from '@shared/utils'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { memo } from 'react'
import { match } from 'ts-pattern'

import { Box, cn, HStack, Typography, VStack } from '@podo-app/design-system-temp'
import {
  BottomNavigationAiLearningIconLottie,
  BottomNavigationHomeIconLottie,
  BottomNavigationLessonIconLottie,
  BottomNavigationMypodoIconLottie,
  BottomNavigationReservationIconLottie,
  BottomNavigationTutorIconLottie,
} from '@podo/assets'
import { useFlagsCompat } from '@podo/flags/react'

import { FEATURE_FLAG_KEYS } from '../../constants'

type Props = {
  className?: string
  type: typeof curriculumTypeSchema.enum.BASIC | typeof curriculumTypeSchema.enum.SMART_TALK
  countryCode?: string | null
}

type NavigationItem = {
  key: 'home' | 'subscribes' | 'tutors' | 'schedule' | 'beta-podolingo' | 'my-podo'
  label: string | React.ReactNode
  Icon: ({ isActive }: { isActive: boolean }) => React.ReactNode
  path: string
}

const HomeIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationHomeIconLottie loop={false} play={isActive} />
)
const LessonIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationLessonIconLottie loop={false} play={isActive} />
)
const ReservationIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationReservationIconLottie loop={false} play={isActive} />
)
const AiLearningIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationAiLearningIconLottie loop={false} play={isActive} />
)
const MyPodoIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationMypodoIconLottie loop={false} play={isActive} />
)
const TutorIcon = ({ isActive }: { isActive: boolean }) => (
  <BottomNavigationTutorIconLottie loop={false} play={isActive} />
)

export const shouldShowAiLearningNavigation = (countryCode?: string | null) =>
  isAiLearningAvailableForCountry(countryCode)

export const BottomNavigation = memo(({ className, type, countryCode }: Props) => {
  const t = useTranslation()
  const pathname = usePathname() || ''
  const { [FEATURE_FLAG_KEYS.TUTOR_CONNECT_ALLOWED]: tutorConnectFlag } = useFlagsCompat([
    FEATURE_FLAG_KEYS.TUTOR_CONNECT_ALLOWED,
  ])
  const getIsActive = (path: string) => {
    // For exact path matching to avoid false positives
    // e.g., /home should not match /homepage
    if (path === '/home' || path === '/my-podo' || path === '/subscribes') {
      return pathname === path || pathname.startsWith(path + '/')
    }
    // For legacy paths with more specific prefixes
    return pathname.startsWith(path)
  }

  const navigationItems: NavigationItem[] = [
    {
      key: 'home',
      label: t('navigation.bottom.home'),
      Icon: HomeIcon,
      path: match(type)
        .with(curriculumTypeSchema.enum.BASIC, () => '/home')
        .with(curriculumTypeSchema.enum.SMART_TALK, () => '/home/ai')
        .exhaustive(),
    },
    {
      key: 'subscribes',
      label: t('navigation.bottom.lessons'),
      Icon: LessonIcon,
      path: '/subscribes',
    },
    {
      key: 'schedule',
      label: t('navigation.bottom.reservation'),
      Icon: ReservationIcon,
      path: '/reservation',
    },
    ...(tutorConnectFlag.enabled
      ? [
          {
            key: 'tutors' as const,
            label: t('navigation.bottom.tutors'),
            Icon: TutorIcon,
            path: '/tutors',
          },
        ]
      : []),
    ...(shouldShowAiLearningNavigation(countryCode)
      ? [
          {
            key: 'beta-podolingo' as const,
            label: t('navigation.bottom.aiLearning'),
            Icon: AiLearningIcon,
            path: '/ai-learning',
          },
        ]
      : []),
    {
      key: 'my-podo',
      label: t('navigation.bottom.myPodo'),
      Icon: MyPodoIcon,
      path: '/my-podo',
    },
  ]

  return (
    <Box className={cn('w-full bg-white border-t border-gray-200 sticky bottom-0 z-10', className)}>
      <Box className="w-full mx-auto">
        <HStack className="w-full h-[60px] justify-between items-stretch">
          {navigationItems.map((item) => {
            const isActive = getIsActive(item.path)

            return (
              <Link key={item.key} href={item.path} className="flex-1" data-testid={`nav-${item.key}`}>
                <VStack className="flex-1 items-center justify-center gap-1.5 p-[9px] cursor-pointer">
                  <Box
                    className={cn('w-6 h-6 flex items-center justify-center', {
                      'text-black': isActive,
                      'text-gray-300': !isActive,
                    })}
                  >
                    <item.Icon key={isActive ? 'active' : 'inactive'} isActive={isActive} />
                  </Box>
                  <Typography
                    size="caption"
                    className={cn(
                      'font-semibold text-[9px] tracking-tighter flex items-center gap-0.5',
                      isActive ? 'text-black' : 'text-gray-300',
                    )}
                    asChild={false}
                  >
                    {item.label}
                  </Typography>
                </VStack>
              </Link>
            )
          })}
        </HStack>
      </Box>
    </Box>
  )
})
